import { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Skeleton,
  Stack
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { WishlistService } from '../services/WishlistService';
import { CartService } from '../services/CartService';
import { ProductService } from '../services/ProductService';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Image as ImageIcon,
  Favorite as FavoriteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export default function Wishlist() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // First get the wishlist IDs
  const { data: wishlistData, isLoading: wishlistLoading, error: wishlistError } = useQuery(
    'wishlist',
    () => WishlistService.getWishlist(),
    {
      refetchOnWindowFocus: false
    }
  );

  // Then get the product details for each ID
  const { data: products, isLoading: productsLoading, error: productsError } = useQuery(
    ['wishlistProducts', wishlistData?.items],
    async () => {
      if (!wishlistData?.items?.length) return [];
      const productDetails = await Promise.all(
        wishlistData.items.map(id => ProductService.getProductById(id))
      );
      return productDetails;
    },
    {
      enabled: !!wishlistData?.items?.length
    }
  );

  const removeFromWishlistMutation = useMutation(
    (productId) => WishlistService.removeFromWishlist(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wishlist');
        enqueueSnackbar(t('wishlist.removeSuccess'), { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar(t('wishlist.removeError'), { variant: 'error' });
      }
    }
  );

  const addToCartMutation = useMutation(
    (productId) => CartService.addToCart(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cart');
        enqueueSnackbar(t('wishlist.addToCartSuccess'), { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar(t('wishlist.addToCartError'), { variant: 'error' });
      }
    }
  );

  const isLoading = wishlistLoading || productsLoading;
  const error = wishlistError || productsError;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  if (!products?.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box textAlign="center">
          <FavoriteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('wishlist.empty')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/search')}
            sx={{ mt: 2 }}
          >
            {t('wishlist.startShopping')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('wishlist.title')}
      </Typography>
      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item key={product.id} xs={12} sm={6} md={4}>
            <Card>
              {product.images?.length > 0 ? (
                <CardMedia
                  component="img"
                  height="200"
                  image={product.images[0]}
                  alt={product.title}
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100'
                  }}
                >
                  <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                </Box>
              )}
              <CardContent>
                <Typography gutterBottom variant="h6" component="h2">
                  {product.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    mb: 2
                  }}
                >
                  {product.description}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    label={t(`product.categories.${product.category.toLowerCase()}`)}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={t(`product.conditions.${product.condition.toLowerCase()}`)}
                    color={product.condition === 'New' ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="h6" color="primary">
                  ${product.price?.toFixed(2)}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                <IconButton
                  color="error"
                  onClick={() => removeFromWishlistMutation.mutate(product.id)}
                >
                  <DeleteIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<ShoppingCartIcon />}
                  onClick={() => addToCartMutation.mutate(product.id)}
                  size="small"
                >
                  {t('wishlist.addToCart')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
} 